import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { Button } from '@/components/ui';
import type { MilestoneView } from '@/types/current-project';
import type { TaskDefinition } from '@/types/project-command';
import type { useProjectCommand } from '@/hooks/useProjectCommand';

export function PlanningTaskFields({
  proposalRevision,
  milestone,
  projectId,
  command,
  disabled,
  onDirty,
  draftKey,
}: {
  proposalRevision?: number;
  milestone?: MilestoneView;
  draftKey?: string;
  projectId: string;
  command: ReturnType<typeof useProjectCommand>;
  disabled: boolean;
  onDirty?: (dirty: boolean) => void;
}) {
  const [edits, setEdits] = useState<Record<number, boolean>>({});
  const principal = useAuthStore((s) => s.session?.principalId);
  const localKey =
    draftKey ??
    'planning-tasks:' +
      principal +
      ':' +
      projectId +
      ':' +
      milestone?.taskStorage.taskStorageId;
  const [adding, setAdding] = useState(
    () => sessionStorage.getItem(localKey + ':new') !== null
  );
  const [newDirty, setNewDirty] = useState(
    () => !!sessionStorage.getItem(localKey + ':new')
  );
  const tasks = milestone?.taskStorage.tasks ?? [];
  const dirty = newDirty || tasks.some((task) => edits[task.taskId]);
  useEffect(() => {
    onDirty?.(dirty);
  }, [dirty, onDirty]);
  return (
    <div className="tasksRow">
      <div className="flex items-center justify-between gap-4">
        <h4 className="text-base font-semibold">
          Deliverables / Tasks{' '}
          <span className="text-xs text-[--text-dark-tertiary]">
            ({tasks.length + (adding ? 1 : 0)})
          </span>
        </h4>
        <Button
          variant="ghost"
          size="sm"
          disabled={command.busy || command.pending || adding}
          onClick={() => {
            setAdding(true);
            sessionStorage.setItem(localKey + ':new', '');
          }}
        >
          <i
            aria-hidden="true"
            className="ri-add-line text-sm text-[--state-brand-active]"
          />{' '}
          Add Task
        </Button>
      </div>
      {tasks.map((entry, index) => (
        <TaskField
          key={entry.taskId}
          localKey={localKey + ':' + entry.taskId}
          number={index + 1}
          task={entry.task}
          taskId={entry.taskId}
          proposalRevision={proposalRevision}
          storageId={milestone?.taskStorage.taskStorageId}
          projectId={projectId}
          command={command}
          disabled={disabled}
          onDirty={(dirty) =>
            setEdits((previous) =>
              previous[entry.taskId] === dirty
                ? previous
                : { ...previous, [entry.taskId]: dirty }
            )
          }
        />
      ))}
      {!milestone && (
        <p className="text-xs text-[--text-dark-tertiary]">
          Save the draft before saving its tasks.
        </p>
      )}
      {adding && (
        <TaskField
          localKey={localKey + ':new'}
          number={tasks.length + 1}
          storageId={milestone?.taskStorage.taskStorageId}
          projectId={projectId}
          command={command}
          disabled={disabled}
          onDirty={setNewDirty}
          onDone={() => {
            setAdding(false);
            setNewDirty(false);
          }}
        />
      )}
    </div>
  );
}
function TaskField({
  number,
  task,
  taskId,
  proposalRevision,
  storageId,
  localKey,
  projectId,
  command,
  disabled,
  onDone,
  onDirty,
}: {
  number: number;
  task?: TaskDefinition;
  taskId?: number;
  proposalRevision?: number;
  storageId?: string;
  localKey: string;
  projectId: string;
  command: ReturnType<typeof useProjectCommand>;
  disabled: boolean;
  onDone?: () => void;
  onDirty?: (dirty: boolean) => void;
}) {
  const [text, setText] = useState<string | null>(() =>
    sessionStorage.getItem(localKey)
  );
  const [source, setSource] = useState(
    () =>
      sessionStorage.getItem(localKey + ':source') ??
      JSON.stringify(task ?? null)
  );
  useEffect(() => {
    onDirty?.(
      text !== null &&
        text !== (task?.title ?? '') &&
        (task != null || !!text.trim())
    );
  }, [text, task, onDirty]);
  const changed = text !== null && source !== JSON.stringify(task ?? null);
  const locked = disabled || command.busy || command.pending || command.blocked;
  const title = text ?? task?.title ?? '';
  return (
    <form
      aria-label={'Milestone task ' + number}
      onSubmit={(event) => {
        event.preventDefault();
        if (locked || changed || !storageId || !title.trim()) return;
        const payload: TaskDefinition = {
          ...(task ?? {
            description: '',
            taskType: 'Task',
            priority: 'Medium',
            status: 'ToDo',
            assignees: [],
            estimatedMinutes: 0,
            loggedMinutes: 0,
            dueAt: null,
          }),
          title,
        };
        void command
          .run(
            taskId == null
              ? { kind: 'task-create', projectId, storageId, payload }
              : { kind: 'task-update', projectId, storageId, taskId, payload }
          )
          .then((result) => {
            if (result) {
              sessionStorage.removeItem(localKey);
              sessionStorage.removeItem(localKey + ':source');
              setText(null);
              onDirty?.(false);
              onDone?.();
            }
          });
      }}
    >
      <fieldset
        disabled={command.busy || command.pending || command.blocked || changed}
        className="flex min-w-0 items-center gap-[--spacing-6]"
      >
        <span className="shrink-0 text-xs font-medium text-[--text-dark-tertiary]">
          #{number}
        </span>
        <input
          aria-label={'Task ' + number}
          placeholder={'Task ' + number + ' description...'}
          required
          className="input min-w-0 flex-1"
          value={title}
          onChange={(event) => {
            if (text === null) setSource(JSON.stringify(task ?? null));
            sessionStorage.setItem(localKey, event.target.value);
            sessionStorage.setItem(localKey + ':source', source);
            setText(event.target.value);
            onDirty?.(
              event.target.value !== (task?.title ?? '') &&
                (task != null || !!event.target.value.trim())
            );
          }}
        />
        <Button
          type="submit"
          variant="outline"
          size="sm"
          disabled={
            locked ||
            !storageId ||
            !title.trim() ||
            (taskId != null && text === null)
          }
          aria-label={'Save task ' + number}
        >
          <i aria-hidden="true" className="ri-save-line" />
        </Button>
        {taskId != null && storageId && proposalRevision != null && (
          <Button
            variant="outline"
            size="sm"
            disabled={locked || changed}
            aria-label={'Remove task ' + number}
            onClick={() =>
              void command
                .run({
                  kind: 'task-delete',
                  projectId,
                  storageId,
                  taskId,
                  payload: { expectedRevision: proposalRevision },
                })
                .then((result) => {
                  if (result) {
                    sessionStorage.removeItem(localKey);
                    sessionStorage.removeItem(localKey + ':source');
                    setText(null);
                    onDirty?.(false);
                  }
                })
            }
          >
            <i aria-hidden="true" className="ri-delete-bin-line" />
          </Button>
        )}
        {taskId == null && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              sessionStorage.removeItem(localKey);
              sessionStorage.removeItem(localKey + ':source');
              onDone?.();
            }}
            aria-label="Remove unsaved task"
          >
            <i aria-hidden="true" className="ri-delete-bin-line" />
          </Button>
        )}
      </fieldset>
      {task?.description && (
        <p className="mt-2 whitespace-pre-wrap text-sm text-[--text-dark-secondary]">
          {task.description}
        </p>
      )}
      {changed && (
        <div role="alert">
          <p>This task changed on the server. Reload it before saving.</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              sessionStorage.removeItem(localKey);
              sessionStorage.removeItem(localKey + ':source');
              setText(null);
              setSource(JSON.stringify(task ?? null));
              onDirty?.(false);
            }}
          >
            Reload task
          </Button>
        </div>
      )}
    </form>
  );
}
